import React, { useState, useEffect } from 'react';
import { Award, Mail, Github, Gitlab, Linkedin, ExternalLink, Medal, Rocket } from 'lucide-react';
// import emailjs from '@emailjs/browser';

// Custom Audio Lines Component
const AudioLines = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <rect x="2" y="8" width="2" height="8" rx="1" />
    <rect x="6" y="4" width="2" height="16" rx="1" />
    <rect x="10" y="6" width="2" height="12" rx="1" />
    <rect x="14" y="2" width="2" height="20" rx="1" />
    <rect x="18" y="7" width="2" height="10" rx="1" />
    <rect x="22" y="5" width="2" height="14" rx="1" />
  </svg>
);

// Custom Hugging Face Icon Component
const HuggingFaceIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-3-9c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zm6 0c.55 0 1-.45 1-1s-.45-1-1-1-1 .45-1 1 .45 1 1 1zm-3 5.5c2.33 0 4.32-1.45 5.12-3.5H6.88c.8 2.05 2.79 3.5 5.12 3.5z"/>
  </svg>
);

// Job titles array - moved outside component to prevent re-renders
const JOB_TITLES = ['Software Engineer', 'ML Engineer', 'Data Engineer', 'Data Scientist'];

// Typing Effect Component
const TypingEffect = () => {
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const currentTitle = JOB_TITLES[currentTitleIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentTitle.length) {
          setCurrentText(currentTitle.substring(0, currentText.length + 1));
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentText.substring(0, currentText.length - 1));
        } else {
          // Finished deleting, move to next title
          setIsDeleting(false);
          setCurrentTitleIndex((prevIndex) => (prevIndex + 1) % JOB_TITLES.length);
        }
      }
    }, isDeleting ? 50 : currentText.length === currentTitle.length ? 0 : 100);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentTitleIndex]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorTimeout = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorTimeout);
  }, []);

  return (
    <span>
      {currentText}
      <span className={`inline-block w-0.5 h-8 bg-purple-400 ml-1 ${showCursor ? 'opacity-100' : 'opacity-0'}`}>
        |
      </span>
    </span>
  );
};

function Portfolio() {
  const [showAllProjects, setShowAllProjects] = useState(false);
  
  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({
        type: 'error',
        message: 'Please fill in all fields.'
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: 'error',
        message: 'Please enter a valid email address.'
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    
    try {
      // For now, create a mailto link as fallback
      const subject = encodeURIComponent(`Contact from ${formData.name}`);
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n\n` +
        `Message:\n${formData.message}`
      );
      
      // Open email client
      window.location.href = `mailto:realjustinzheng@gmail.com?subject=${subject}&body=${body}`;
      
      setSubmitStatus({
        type: 'success',
        message: 'Opening your email client... Please send the email to complete your message.'
      });
      
      // Reset form after a delay
      setTimeout(() => {
        setFormData({ name: '', email: '', message: '' });
        setSubmitStatus({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Please contact me directly at realjustinzheng@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const projects = [
    {
      image: "images/Speaker-Recognition-Profile.png",
      title: "Automatic Speaker Recognition and Profiling	",
      description: "Speaker Recognition and Profiling for real-time transcriptions and diarizations.",
      tech: ["Pynnanote", "Speechbrain", "Whisper-STT"],
      link: "https://github.com/JustinTheGreat/Speaker-Profiles"
    },
    {
      image: "images/OCR-PDF-Analyzer.png",
      title: "OCR PDF Analysis Framework",
      description: "Local OCR based framework ETL with dynamic settings to extract data from PDFs into multiple usable data formnats (JSONS/Excel etc..)",
      tech: ["OCR", "ETL/ELT", "Data Processing"],
      link: "https://github.com/JustinTheGreat/PDF_To_Excel"
    },
    {
      image: "images/super-siri.png",
      title: "Super Siri",
      description: "Analyzes user conversation audio, collected via a wireless mic and stored in a database, using an AI API to generate context-specific to-do lists and project tracking for work performance",
      tech: ["GCP", "NLP", "Raspberry Pi"],
      link: "https://github.com/JustinTheGreat/super_siri"
    },
    {
      image: "images/meeting-pager.jpg",
      title: "Meeting Pager",
      description: "Pager system that uses keyword detection to alert you of important subjects or mentions of your name, so you can zone out and focus on the more important things",
      tech: ["Twilio", "Gen AI", "Pyaudio"],
      link: "https://github.com/Laziness-101/pager"
    },
    {
      image: "images/quantdog.jpg",
      title: "Quantdog",
      description: "Intelligent quantum threat detection and adaptive cryptographic routing platform that protects cryptocurrency transactions from quantum computer attacks",
      tech: [ "Blockchain","Quantum Computing", "Cryptography"],
      link: "https://github.com/boshyxd/QuantDog"
    },
    {
      image: "images/prohibere.jpg",
      title: "Prohibere",
      description: "AI Voice assistant to detect and blacklist scam callers via questioning and contextual learning",
      tech: ["Twilio", "React Native", "Ngrok"],
      link: "https://github.com/JUSTINSGRANDMAMA/Prohibere"
    }
  ];

  const visibleProjects = showAllProjects ? projects : projects.slice(0, 3);

  const achievements = [
    {
      icon: <Medal className="w-8 h-8" />,
      title: "10+ Total Hackathon Wins",
      description: "Won a variety hackathons from Michigan-Toronto Region"
    },
    {
      icon: <Rocket className="w-8 h-8" />,
      title: "Featured Global Nominee",
      description: "NASA Spaces Hacks Featured as a Global Nominee"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Awarded as Most Improved",
      description: "Awarded by Stellantis/Chrysler"
    }
  ];

  const experiences = [
    {
      company: "Academy Glass",
      role: "Full Stack Developer",
      period: "May 2024 - Present",
      description: [
        "Developed a payment default dashboard that improved proactive customer response by 30% through the visualization and analysis of customer cashflow patterns.",
        "Architected an end-to-end AWS data pipeline to automate sales analytics for a glass manufacturer, converting raw invoice data into actionable business intelligence.",
        "Leveraged Apache Spark for large-scale data wrangling, delivering a high-quality structured dataset that improved the speed and accuracy of downstream analysis.",
        "Orchestrated an end-to-end data workflow with Apache Airflow, ensuring reliable and automated daily data delivery from ingestion to reporting.",
        "Developed interactive dashboards in Power BI to visualize key business metrics, including customer retention rates and invoice delinquency risk."
      ],
      icon: "🏠"
    },
    {
      company: "Stellantis - Battery Pack Testing",
      role: "Data Engineer Intern",
      period: "April 2024 - Present",
      description: [
        "Developed a PDF ELT analytical framework that streamlined report generation and reduced manual effort.",
        "Meet with stakeholders to help migrate systems over to utilize snowflake and databricks for data warehousing, ETL/ELTs, & processing system.",
        "Utilize AVL engineering framework to generate data analytics reports and complete backlog items.",
        "Automated data processing with python applications.",
        "Created software solutions process to make documentation more accessible."
      ],
      icon: "🔋"
    },
    {
      company: "Stellantis - Chrysler ARDC",
      role: "IT Specialist Intern",
      period: "Aug 2023- Aug 2024",
      description: [
        "Contribute to MS Sharepoint web development.",
        "Support electrical validation engineers with vehicle software testing.",
        "Maintain and provide support for existing Red Hat linux systems.",
        "Utilized Azure Active Directory and existing PowerShell scripts to customize clients Office 365 portal.",
        "Managed the IT and network infrastructure for a new battery R&D lab throughout its build-out and launch."
      ],
      icon: "🚙"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-slate-950/80 backdrop-blur-md z-50 border-b border-purple-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <AudioLines className="w-6 h-6" />
            </div>
          </div>
          <div className="flex gap-2">
            <a href="#projects" className="no-underline px-4 py-2 rounded-lg font-medium text-gray-300 hover:text-white hover:bg-purple-500/20 border border-transparent hover:border-purple-500/30 transition-colors transition-bg transition-border duration-300 ease-in-out">Projects</a>
            <a href="#experience" className="no-underline px-4 py-2 rounded-lg font-medium text-gray-300 hover:text-white hover:bg-purple-500/20 border border-transparent hover:border-purple-500/30 transition-colors transition-bg transition-border duration-300 ease-in-out">Experience</a>
            <a href="#contact" className="no-underline px-4 py-2 rounded-lg font-medium text-gray-300 hover:text-white hover:bg-purple-500/20 border border-transparent hover:border-purple-500/30 transition-colors transition-bg transition-border duration-300 ease-in-out">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-7xl font-bold mb-4 pb-2 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent" style={{
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Hey I'm Justin
            </span>
          </h1>
          <p className="text-3xl text-gray-300 mb-16">
            <TypingEffect />
          </p>
          
          <a 
            href="https://gitlab.com/justinthegreat-group/transcription-speaker-saver" 
            target="_blank" 
            rel="noopener noreferrer"
            className="block no-underline hover:transform hover:scale-105 transition-all duration-300 cursor-pointer"
          >
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40 rounded-2xl p-8 backdrop-blur-sm hover:bg-gradient-to-r hover:from-purple-500/15 hover:to-pink-500/15 transition-all duration-300">
              <p className="text-xl text-gray-200 mb-6">
                Currently working on imitating human speech patterns & memories with AI
              </p>
              <div className="w-full h-64 rounded-xl overflow-hidden">
                <img 
                  src="images/current-project.png" 
                  alt="Current Project - Transcription Speaker Saver"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-4">My Work</h2>
          <p className="text-gray-400 mb-12">Exploring the intersection of voice, AI, and human interaction</p>
          
          <div className="mb-8">
            <span className="inline-block bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-semibold">
              Featured
            </span>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-8">
            {visibleProjects.map((project, idx) => (
              <div key={idx} className="bg-slate-900/50 border border-purple-500/20 rounded-xl overflow-hidden hover:border-purple-500/40 transition-all hover:transform hover:scale-105 duration-300">
                <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-gray-400 text-sm mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, i) => (
                      <span key={i} className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <a 
                    href={project.link}
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Try Now <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {projects.length > 3 && (
            <div className="text-center">
              <button
                onClick={() => setShowAllProjects(!showAllProjects)}
                className="bg-purple-500 hover:bg-purple-600 px-8 py-3 rounded-full font-semibold transition-colors"
              >
                {showAllProjects ? 'Show Less' : 'View All Projects'}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-16">Experience</h2>
          <div className="relative">
            {/* Timeline Line */}
            <div className="timeline-line"></div>
            
            {experiences.map((exp, idx) => (
              <div key={idx} className={`flex items-center mb-16 ${idx % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                {/* Content Card */}
                <div className={`w-5/12 ${idx % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/40 transition-all">
                    <h3 className="text-2xl font-bold text-white mb-1">{exp.role}</h3>
                    <p className="text-sm text-gray-400 mb-4">{exp.company}</p>
                    <ul className={`space-y-3 text-gray-300 text-sm ${idx % 2 === 0 ? 'text-right' : 'text-left'}`}>
                      {exp.description.map((point, i) => (
                        <li key={i} className="leading-relaxed">{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Timeline Node */}
                <div className="w-2/12 flex justify-center relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-2xl border-4 border-slate-950">
                    {exp.icon}
                  </div>
                </div>

                {/* Date */}
                <div className={`w-5/12 ${idx % 2 === 0 ? 'text-left pl-8' : 'text-right pr-8'}`}>
                  <p className="text-gray-400 text-lg font-semibold">{exp.period}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold mb-12">Notable Achievements</h2>
          <div className="grid grid-cols-3 gap-6">
            {achievements.map((achievement, idx) => (
              <div key={idx} className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-8 text-center hover:border-purple-500/40 transition-all">
                <div className="flex justify-center mb-4 text-purple-400">
                  {achievement.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2">{achievement.title}</h3>
                <p className="text-gray-400 text-sm">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-5xl font-bold mb-12 text-center">Get In Touch</h2>
          
          {/* Status Messages */}
          {submitStatus.message && (
            <div className={`mb-6 p-4 rounded-lg text-center ${
              submitStatus.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/40 text-green-300' 
                : 'bg-red-500/20 border border-red-500/40 text-red-300'
            }`}>
              {submitStatus.message}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">Name</label>
              <input 
                type="text" 
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full bg-slate-900/50 border border-purple-500/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500/40 transition-colors"
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Email</label>
              <input 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full bg-slate-900/50 border border-purple-500/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500/40 transition-colors"
                placeholder="your@email.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Message</label>
              <textarea 
                rows={5}
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="w-full bg-slate-900/50 border border-purple-500/20 rounded-lg px-4 py-3 focus:outline-none focus:border-purple-500/40 transition-colors resize-none"
                placeholder="Your message..."
                required
              />
            </div>
            <button 
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                isSubmitting 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-purple-500 hover:bg-purple-600 cursor-pointer'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-purple-500/20">
        <div className="max-w-6xl mx-auto flex justify-center gap-8">
          <a href="mailto:realjustinzheng@gmail.com" className="text-gray-400 hover:text-purple-400 transition-colors">
            <Mail className="w-6 h-6" />
          </a>
          <a href="https://github.com/JustinTheGreat" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition-colors">
            <Github className="w-6 h-6" />
          </a>
          <a href="https://gitlab.com/justinthegreat-group" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition-colors">
            <Gitlab className="w-6 h-6" />
          </a>
          <a href="https://www.linkedin.com/in/justin-zheng-21a1b7210/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition-colors">
            <Linkedin className="w-6 h-6" />
          </a>
          <a href="https://huggingface.co/FrozenCrusader" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-400 transition-colors">
            <HuggingFaceIcon className="w-6 h-6" />
          </a>
        </div>
      </footer>
    </div>
  );
}

export default Portfolio;
